import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";

const slides = [
  {
    image: "https://public.readdy.ai/ai/img_res/7c3bc615a70df4641f6f66d3fc106d39.jpg",
    tag: "Signature Balayage",
    headline: "Where European Artistry\nMeets Vegas Glamour",
    sub: "Precision cuts, luminous color, and transformative styles crafted by masters of the European tradition.",
  },
  {
    image: "https://readdy.ai/api/search-image?query=glamorous%20woman%20with%20rich%20deep%20brunette%20hair%20waves%2C%20professional%20hair%20styling%20luxury%20salon%2C%20Las%20Vegas%20high-end%20beauty%2C%20dramatic%20lighting%2C%20gold%20and%20black%20tones%2C%20magazine%20editorial%20portrait%2C%20sophisticated%20and%20confident%2040s%20woman&width=1920&height=1080&seq=hero2&orientation=landscape",
    tag: "Couture Color",
    headline: "Luxury Color Crafted\nFor Your Lifestyle",
    sub: "From sun-kissed highlights to bold transformations — every shade is a work of art.",
  },
  {
    image: "https://readdy.ai/api/search-image?query=elegant%20woman%20with%20sleek%20sophisticated%20updo%20hairstyle%20formal%20evening%20luxury%20Las%20Vegas%20casino%20hotel%2C%20gold%20jewelry%2C%20black%20dress%2C%20Hollywood%20glamour%2C%20warm%20glowing%20editorial%20lighting%2C%20beauty%20perfection%2C%20high%20fashion%20photography&width=1920&height=1080&seq=hero3&orientation=landscape",
    tag: "Event & Bridal",
    headline: "Look Unforgettable\nFor Every Occasion",
    sub: "Bridal, gala, or a night on the Strip — we make sure every detail is flawless.",
  },
];

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback((idx: number) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(idx);
      setAnimating(false);
    }, 400);
  }, [animating]);

  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo]);
  const prev = useCallback(() => goTo((current - 1 + slides.length) % slides.length), [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = slides[current];

  return (
    <section className="relative w-full h-[100vh] min-h-[640px] overflow-hidden">
      {/* Background images */}
      {slides.map((s, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-700 ${i === current ? "opacity-100" : "opacity-0"}`}
        >
          <img
            src={s.image}
            alt={s.tag}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20"></div>

      {/* Content */}
      <div className={`absolute inset-0 flex flex-col justify-center px-8 md:px-20 lg:px-32 transition-all duration-500 ${animating ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"}`}>
        <span className="text-[#C9A96E] text-xs font-semibold tracking-[0.3em] uppercase mb-4 block">{slide.tag}</span>
        <h1 className="text-white text-4xl md:text-6xl lg:text-7xl font-bold leading-tight whitespace-pre-line mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
          {slide.headline}
        </h1>
        <p className="text-white/80 text-base md:text-lg max-w-xl mb-10 leading-relaxed">{slide.sub}</p>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link
            to="/booking"
            className="bg-[#C9A96E] text-white text-sm font-semibold tracking-widest uppercase px-10 py-4 rounded-sm hover:bg-[#b8934f] transition-colors duration-200 whitespace-nowrap cursor-pointer text-center"
          >
            Book Your Appointment
          </Link>
          <Link
            to="/portfolio"
            className="border border-white/60 text-white text-sm font-semibold tracking-widest uppercase px-10 py-4 rounded-sm hover:border-[#C9A96E] hover:text-[#C9A96E] transition-colors duration-200 whitespace-nowrap cursor-pointer text-center"
          >
            View Portfolio
          </Link>
        </div>
      </div>

      {/* Arrows */}
      <button
        onClick={prev}
        className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full border border-white/40 text-white hover:border-[#C9A96E] hover:text-[#C9A96E] transition-colors cursor-pointer backdrop-blur-sm bg-black/20"
        aria-label="Previous"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={next}
        className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full border border-white/40 text-white hover:border-[#C9A96E] hover:text-[#C9A96E] transition-colors cursor-pointer backdrop-blur-sm bg-black/20"
        aria-label="Next"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      {/* Dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`cursor-pointer transition-all duration-300 rounded-full ${i === current ? "bg-[#C9A96E] w-8 h-2" : "bg-white/40 w-2 h-2 hover:bg-white/70"}`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Scroll hint */}
      <div className="absolute bottom-8 right-8 hidden md:flex flex-col items-center gap-2 text-white/40">
        <span className="text-xs tracking-widest uppercase" style={{ writingMode: "vertical-rl" }}>Scroll</span>
        <i className="ri-arrow-down-line text-sm"></i>
      </div>
    </section>
  );
}
